// useTripDraft — form + flight-list + sub-form UI state hook for ManualTripCreator.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (behavior-preserving
// refactor, issue #240). Owns every piece of the manual-trip "draft" UI state
// the source held inline:
//   - `formData`            (initialised from `getEmptyForm()`)
//   - `error`               (the inline validation/auth/API error string)
//   - `flights`             (the added-flights list)
//   - `showFlightLookup`    (the flight-number lookup sub-form toggle)
//   - `showFlightSearch`    (the route-search sub-form toggle)
//   - `isMobile`            (responsive breakpoint, `window.innerWidth < 480`)
//
// Every handler reproduces the source EXACTLY:
//   - `handleInputChange(e)` updates the named field and clears `error` only
//     when one is currently set (`if (error) setError('')`).
//   - the viewport effect adds a `resize` listener on mount (initial + updates
//     to `window.innerWidth < 480`) and removes it on unmount.
//   - `handleReset()` resets the form to `getEmptyForm()`, clears `error`, empties
//     `flights`, and closes the lookup sub-form. It does NOT touch
//     `showFlightSearch` — preserved verbatim from the source's `handleReset`.
//   - `handleRemoveFlight(index)` drops the flight at `index`.
//   - `onFlightFound(flight)` appends the flight to `flights`.
//   - `openSearch` / `openLookup` / `cancelSearch` / `cancelLookup` toggle the two
//     sub-form booleans exactly as the source's inline handlers did.
//   - `getDefaultDepartureAirport` / `getDefaultArrivalAirport` derive the
//     airport code from the start/end destination via `extractAirportCode`.
//
// The raw setters `setFormData`, `setError`, `setFlights`, and
// `setShowFlightLookup` are also exposed so the create hook (useCreateTrip) can
// surface errors and perform the post-create form reset, matching the source's
// shared closure over these setters. The container (task 14.7) wires them.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useEffect } from 'react';
import getEmptyForm from '../logic/getEmptyForm.js';
import extractAirportCode from '../logic/extractAirportCode.js';

/**
 * Owns the manual-trip form, added-flights list, sub-form toggles, and
 * responsive breakpoint state.
 *
 * @returns {{
 *   formData: Object,
 *   setFormData: Function,
 *   error: string,
 *   setError: Function,
 *   flights: Array<Object>,
 *   setFlights: Function,
 *   showFlightLookup: boolean,
 *   setShowFlightLookup: Function,
 *   showFlightSearch: boolean,
 *   isMobile: boolean,
 *   handleInputChange: (e: Object) => void,
 *   handleReset: () => void,
 *   handleRemoveFlight: (index: number) => void,
 *   onFlightFound: (flight: Object) => void,
 *   openSearch: () => void,
 *   openLookup: () => void,
 *   cancelSearch: () => void,
 *   cancelLookup: () => void,
 *   getDefaultDepartureAirport: () => string,
 *   getDefaultArrivalAirport: () => string
 * }} The draft state and actions.
 */
export function useTripDraft() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  const [formData, setFormData] = useState(getEmptyForm());
  const [error, setError] = useState('');
  const [flights, setFlights] = useState([]);
  const [showFlightLookup, setShowFlightLookup] = useState(false);
  const [showFlightSearch, setShowFlightSearch] = useState(false);

  // Track viewport width for responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleReset = () => {
    setFormData(getEmptyForm());
    setError('');
    setFlights([]);
    setShowFlightLookup(false);
  };

  const handleRemoveFlight = (index) => {
    setFlights(prev => prev.filter((_, i) => i !== index));
  };

  const onFlightFound = (flight) => {
    setFlights(prev => [...prev, flight]);
  };

  const openSearch = () => setShowFlightSearch(true);
  const openLookup = () => setShowFlightLookup(true);
  const cancelSearch = () => setShowFlightSearch(false);
  const cancelLookup = () => setShowFlightLookup(false);

  const getDefaultDepartureAirport = () => extractAirportCode(formData.startDestination);
  const getDefaultArrivalAirport = () => extractAirportCode(formData.endDestination);

  return {
    formData,
    setFormData,
    error,
    setError,
    flights,
    setFlights,
    showFlightLookup,
    setShowFlightLookup,
    showFlightSearch,
    isMobile,
    handleInputChange,
    handleReset,
    handleRemoveFlight,
    onFlightFound,
    openSearch,
    openLookup,
    cancelSearch,
    cancelLookup,
    getDefaultDepartureAirport,
    getDefaultArrivalAirport
  };
}

export default useTripDraft;
